import React, { useEffect, useMemo, useState } from 'react';
import { Answer, CreateSurveyArgs, SurveyComponent, SurveyConfig, SurveyInstance, SurveyInstanceRenderer, LocationComponent } from './Survey.types';
import { SurveyProvider, useSurvey } from './Survey.provider';
import { Box, CheckboxV2, FormControl, InputV2, TextArea, SelectV1, DatePickerInput } from '@withjoy/joykit';
import {
  StyledSurveyButtonWrapper,
  StyledSurveyChexboxListWrapper,
  StyledSurveyContent,
  StyledSurveyDescription,
  StyledSurveyError,
  StyledSurveyTitle,
  StyledSurveyWrapper,
} from './Survey.styles';
import { addError } from '@shared/utils/logger';
import { useTranslation } from '@shared/core';
import { useAuth } from '@shared/components/AuthProvider';
import { MultiSelectRenderer, SectionTitleRenderer } from './components';
import { LocationInputV2 } from '../LocationInputV2/LocationInputV2';
import { LocationInputV2Response } from '../LocationInputV2/LocationInputV2.types';

const getPlacePredictionTypesForAllowedPlaces = (allowedPlaces?: 'eventVenue' | 'eventLocation' | readonly string[]): string[] => {
  if (!allowedPlaces) {
    return ['country', 'locality', 'administrative_area_level_1', 'administrative_area_level_2', 'lodging', 'point_of_interest'];
  }

  if (Array.isArray(allowedPlaces)) {
    return Array.from(allowedPlaces);
  }

  switch (allowedPlaces) {
    case 'eventVenue':
      return ['street_address', 'premise', 'establishment'];
    case 'eventLocation':
      return ['locality', 'administrative_area_level_1', 'administrative_area_level_2', 'country'];
    default:
      return [];
  }
};

const createSurveyUI = (survey: SurveyInstance) => ({
  Provider: survey.Provider,
  Renderer: survey.Renderer,
});

/**
 * Creates the UI bindings for a survey instance, returning the `Provider` and `Renderer` components.
 *
 * @param config - The survey configuration object.
 * @param onComplete - Callback function to be called when the survey is completed.
 * @param telemetryConfig - Optional telemetry configuration object.
 * @returns An object containing the `Provider` and `Renderer` components for the survey.
 *
 */
export const createSurveyInstance = (args: CreateSurveyArgs) => {
  const survey = createSurvey(args);
  return createSurveyUI(survey);
};

/**
 * Utility function to create a survey configuration object.
 * It takes a configuration object where each key is a step identifier and the value is a StepDefinition.
 *
 * @param config - The configuration object for the survey.
 * @returns The survey configuration object with the correct type.
 *
 */
export const createSurveyConfig = (config: SurveyConfig): SurveyConfig => {
  return config;
};

/**
 * Renders individual survey form components based on their type.
 * Supports: checkbox, datepicker, input, location, multiSelect, radio, sectionTitle, select, and textarea components.
 *
 * Features:
 * - Auto-fill support for user profile data (firstName, lastName, email, phone)
 */
const SurveyFormComponent: React.FC<{
  component: SurveyComponent;
  value: Answer;
  onChange: (value: Answer) => void;
}> = ({ component, value, onChange }) => {
  const hasValue = value !== undefined && value !== null;
  const { currentUser } = useAuth();

  const initialValue = useMemo(() => {
    if (hasValue) return undefined;

    // Auto-fill field with user profile data if autofillType is specified
    if (component.autofillType && currentUser.profile) {
      const { profile } = currentUser;
      switch (component.autofillType) {
        case 'userFirstName':
          return profile.firstName || '';
        case 'userLastName':
          return profile.lastName || '';
        case 'userEmail':
          return profile.email || '';
        default:
          break;
      }
    }

    switch (component.type) {
      case 'input':
      case 'textarea':
        if (typeof component.defaultValue === 'string') {
          return component.defaultValue;
        }
        break;

      case 'radio': {
        const checkedRadioValues = component.options.filter(option => option.checked).map(option => option.value);
        if (checkedRadioValues.length > 0) {
          return checkedRadioValues[0];
        }
        break;
      }

      case 'checkbox': {
        const checkedCheckboxValues = component.options.filter(option => option.checked).map(option => option.value);
        if (checkedCheckboxValues.length > 0) {
          return checkedCheckboxValues;
        }
        break;
      }

      case 'datepicker':
        if (component.defaultValue instanceof Date) {
          return component.defaultValue.toISOString();
        }
        break;

      case 'select':
        if (component.defaultValue && typeof component.defaultValue === 'object' && 'value' in component.defaultValue) {
          return component.defaultValue.value;
        }
        break;

      default:
        return undefined;
    }

    return undefined;
  }, [component, hasValue, currentUser]);

  useEffect(() => {
    if (!hasValue && initialValue !== undefined) {
      onChange(initialValue);
    }
  }, [hasValue, initialValue, onChange]);

  const componentType = component.type;

  switch (componentType) {
    case 'checkbox': {
      const selectedValues = Array.isArray(value) ? value : [];

      return (
        <StyledSurveyChexboxListWrapper flexDirection={'column'}>
          {component.options.map(option => {
            const isChecked = selectedValues.includes(option.value);

            const handleCheckboxChange = () => {
              const newValues = isChecked ? selectedValues.filter(v => v !== option.value) : [...selectedValues, option.value];
              onChange(newValues);
            };

            return (
              <Box key={option.value}>
                <CheckboxV2
                  isChecked={isChecked}
                  isDisabled={option.disabled}
                  onChange={handleCheckboxChange}
                  {...option}
                >
                  {option.label}
                </CheckboxV2>
              </Box>
            );
          })}
        </StyledSurveyChexboxListWrapper>
      );
    }

    case 'datepicker':
      return (
        <DatePickerInput
          value={value ? new Date(value as string) : null}
          onChange={date => onChange(date ? (date instanceof Date ? date.toISOString() : '') : '')}
          enableCloseOnSelect={true}
          {...component}
        />
      );

    case 'input':
      return (
        <InputV2
          value={typeof value === 'string' ? value : ''}
          onChange={e => onChange(e.target.value)}
          {...component}
        />
      );

    case 'location': {
      const { disabled, allowedPlaces, ...rest } = component as LocationComponent;
      const placePredictionTypes = getPlacePredictionTypesForAllowedPlaces(allowedPlaces);

      return (
        <LocationInputV2
          name={component.id}
          placeholder={component.placeholder}
          placePredictionTypes={placePredictionTypes}
          onLocationChange={(location: LocationInputV2Response | null) => {
            onChange(location);
          }}
          defaultValue={typeof value === 'string' ? value : ''}
          disabled={disabled}
          {...rest}
        />
      );
    }

    case 'multiSelect': {
      const multiSelectValue = Array.isArray(value) ? value : [];
      return (
        <MultiSelectRenderer
          component={component}
          value={multiSelectValue}
          onChange={val => onChange(val as string[])}
        />
      );
    }

    case 'radio': {
      const selectedValue = typeof value === 'string' ? value : '';

      return (
        <StyledSurveyChexboxListWrapper flexDirection={'column'}>
          {component.options.map(option => {
            const isChecked = selectedValue === option.value;

            const handleRadioChange = () => {
              onChange(option.value);
            };

            return (
              <Box key={option.value}>
                <CheckboxV2
                  isChecked={isChecked}
                  isDisabled={option.disabled}
                  onChange={handleRadioChange}
                  {...option}
                  overrides={{
                    Control: {
                      props: {
                        borderRadius: 20,
                      },
                    },
                  }}
                >
                  {option.label}
                </CheckboxV2>
              </Box>
            );
          })}
        </StyledSurveyChexboxListWrapper>
      );
    }

    case 'select': {
      const selectOptions = component.options.map(option => ({
        label: option.label,
        value: option.value,
        disabled: option.disabled,
      }));

      return (
        <SelectV1
          {...component}
          options={selectOptions}
          value={selectOptions.find(option => option.value === value)}
          onChange={(selectedOption: { label: string; value: string } | null) => onChange(selectedOption?.value || '')}
        />
      );
    }

    case 'sectionTitle':
      return <SectionTitleRenderer component={component} />;

    case 'textarea': {
      return (
        <TextArea
          value={typeof value === 'string' ? value : ''}
          onChange={e => onChange(e.target.value)}
          {...component}
        />
      );
    }

    default:
      addError(`Unsupported Survey component type ${componentType}`);
      return null;
  }
};

export const createSurvey = (args: CreateSurveyArgs) => {
  const { config, onComplete, telemetryConfig, onFirstStepBack } = args;

  const Renderer: SurveyInstanceRenderer = ({ nextButton: NextButton, backButton: BackButton }) => {
    const { stepConfig, answers, updateAnswer, goNext, goBack, hasNextStep, config } = useSurvey();
    const { components, back, buttons, title, description } = stepConfig;
    const { buttonLayout = 'horizontal' } = config;

    const [hasRequiredError, setHasRequiredError] = useState(false);

    const { t } = useTranslation('survey');
    const buttonsTranslations = t('buttons');
    const errorTranslations = t('errors');
    const { nextLabel, backLabel, submitLabel } = {
      nextLabel: buttons?.nextLabel || config.buttons?.nextLabel || buttonsTranslations.nextLabel(),
      backLabel: buttons?.backLabel || config?.buttons?.backLabel || buttonsTranslations.backLabel(),
      submitLabel: config.buttons?.submitLabel || buttonsTranslations.submitLabel(),
    };
    const requiredErrorLabel = errorTranslations.required();

    const useFirstStepBackLogic = config.initialStep === stepConfig.name && config.buttons?.showBackOnFirstStep && onFirstStepBack;

    // Get custom error message from any component that has one, or use default
    // This allows surveys to override the default "Please complete the field above" message
    const customErrorMessage = components.find(component => component.requiredErrorMessage)?.requiredErrorMessage || requiredErrorLabel;

    const isStepValid = useMemo(
      () =>
        components.every(component => {
          if (!component.required) return true;

          const answer = answers[component.id];

          switch (component.type) {
            case 'checkbox':
              return Array.isArray(answer) && answer.length > 0;

            case 'datepicker':
            case 'input':
            case 'radio':
            case 'select':
            case 'textarea':
              return !!answer && answer !== '';

            case 'location':
              return !!(answer as LocationInputV2Response | null)?.placeId;

            case 'multiSelect':
              return Array.isArray(answer) && answer.length > 0;

            case 'sectionTitle':
              return true;

            default:
              return true;
          }
        }),
      [answers, components],
    );

    const handleGoNext = () => {
      if (!isStepValid) {
        setHasRequiredError(true);
        return;
      }
      goNext();
    };

    const handleGoBack = () => {
      if (useFirstStepBackLogic) {
        onFirstStepBack?.();
      } else {
        goBack();
      }
    };

    return (
      <StyledSurveyWrapper>
        <StyledSurveyContent>
          {title && <StyledSurveyTitle>{title}</StyledSurveyTitle>}
          {description && <StyledSurveyDescription>{description}</StyledSurveyDescription>}
          {components.map(component => {
            const value = answers[component.id];
            const handleChange = (val: Answer) => {
              updateAnswer(component.id, val);
              if (hasRequiredError) {
                setHasRequiredError(false);
              }
            };

            return (
              <FormControl
                label={component.label}
                caption={component.caption}
                key={component.id}
                width={'100%'}
              >
                <SurveyFormComponent
                  component={component}
                  value={value}
                  onChange={handleChange}
                />
              </FormControl>
            );
          })}
          {hasRequiredError && (
            <StyledSurveyError
              color={'red6'}
              fontWeight={'bold'}
            >
              {customErrorMessage}
            </StyledSurveyError>
          )}
        </StyledSurveyContent>
        {/* Button layout: vertical shows Next on top, horizontal shows Back on left */}
        <StyledSurveyButtonWrapper layout={buttonLayout}>
          {buttonLayout === 'vertical' ? (
            <>
              {NextButton && (
                <NextButton
                  goNext={handleGoNext}
                  answers={answers}
                  stepConfig={stepConfig}
                  label={hasNextStep ? nextLabel : submitLabel}
                  disabled={!isStepValid}
                />
              )}
              {(back || useFirstStepBackLogic) && BackButton && (
                <BackButton
                  goBack={handleGoBack}
                  stepConfig={stepConfig}
                  label={backLabel}
                />
              )}
            </>
          ) : (
            <>
              {(back || useFirstStepBackLogic) && BackButton && (
                <BackButton
                  goBack={handleGoBack}
                  stepConfig={stepConfig}
                  label={backLabel}
                />
              )}
              {NextButton && (
                <NextButton
                  goNext={handleGoNext}
                  answers={answers}
                  stepConfig={stepConfig}
                  label={hasNextStep ? nextLabel : submitLabel}
                  disabled={!isStepValid}
                />
              )}
            </>
          )}
        </StyledSurveyButtonWrapper>
      </StyledSurveyWrapper>
    );
  };

  const Provider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <SurveyProvider
      config={config}
      onComplete={onComplete}
      telemetryConfig={telemetryConfig}
    >
      {children}
    </SurveyProvider>
  );

  return {
    Provider,
    Renderer,
  };
};
